import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { BrutalButton } from "./ui/BrutalButton";
import { BrutalCard } from "./ui/BrutalCard";
import { X } from "lucide-react";
import { updateGoogleConsent } from "./TrackingScripts";
import { supabase } from "@/integrations/supabase/client";

export const CookieConsent = () => {
  const { t } = useTranslation();
  const [showBanner, setShowBanner] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true,
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    const consent = localStorage.getItem("cookie-consent");
    if (!consent) {
      setShowBanner(true);
    } else {
      const saved = JSON.parse(consent);
      setPreferences(saved);
    }
  }, []);

  // Listen for global event to reopen cookie settings
  useEffect(() => {
    const handleOpenCookieSettings = () => {
      setShowBanner(true);
      setShowPreferences(true);
    };
    
    window.addEventListener('openCookieSettings', handleOpenCookieSettings);
    return () => window.removeEventListener('openCookieSettings', handleOpenCookieSettings);
  }, []);

  const logCookieConsent = async (analytics: boolean, marketing: boolean) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        supabase.functions.invoke("log-consent", {
          body: {
            consents: [
              { type: "cookies_necessary", granted: true, policy_version: "2025-01-15" },
              { type: "cookies_analytics", granted: analytics, policy_version: "2025-01-15" },
              { type: "cookies_marketing", granted: marketing, policy_version: "2025-01-15" },
            ],
          }
        }).catch(() => {});
      }
    } catch {}
  };

  const acceptAll = () => {
    const allAccepted = {
      necessary: true,
      analytics: true,
      marketing: true,
    };
    localStorage.setItem("cookie-consent", JSON.stringify(allAccepted));
    setPreferences(allAccepted);
    setShowBanner(false);
    updateGoogleConsent(true, true);
    logCookieConsent(true, true);
  };

  const acceptNecessary = () => {
    const necessaryOnly = {
      necessary: true,
      analytics: false,
      marketing: false,
    };
    localStorage.setItem("cookie-consent", JSON.stringify(necessaryOnly));
    setPreferences(necessaryOnly);
    setShowBanner(false);
    updateGoogleConsent(false, false);
    logCookieConsent(false, false);
  };

  const savePreferences = () => {
    localStorage.setItem("cookie-consent", JSON.stringify(preferences));
    setShowBanner(false);
    setShowPreferences(false);
    updateGoogleConsent(preferences.analytics, preferences.marketing);
    logCookieConsent(preferences.analytics, preferences.marketing);
  };

  if (!showBanner) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 p-4 bg-black/50 backdrop-blur-sm">
      <div className="container mx-auto max-w-4xl">
        <BrutalCard className="relative">
          <button
            onClick={() => setShowBanner(false)}
            className="absolute top-4 right-4 p-2 hover:bg-surface rounded"
            aria-label="Chiudi banner cookie"
          >
            <X size={20} />
          </button>

          {!showPreferences ? (
            <div>
              <h2 className="font-heading text-xl mb-3">
                🍪 Utilizziamo i cookie
              </h2>
              <p className="text-sm mb-4 max-w-2xl">
                Utilizziamo cookie necessari per il funzionamento del sito e cookie opzionali 
                per migliorare la tua esperienza e fornire contenuti personalizzati. 
                Puoi accettare tutti i cookie o gestire le tue preferenze.
              </p>
              
              <div className="flex flex-wrap items-center justify-between gap-4">
                {/* Azioni secondarie - link discreti a sinistra */}
                <div className="flex flex-wrap gap-4 text-sm">
                  <button 
                    onClick={acceptNecessary}
                    className="text-foreground/60 hover:text-foreground underline-offset-2 hover:underline transition-colors"
                  >
                    Solo necessari
                  </button>
                  <button 
                    onClick={() => setShowPreferences(true)}
                    className="text-foreground/60 hover:text-foreground underline-offset-2 hover:underline transition-colors"
                  >
                    Gestisci preferenze
                  </button>
                </div>
                
                {/* Azione primaria - bottone prominente a destra */}
                <BrutalButton onClick={acceptAll}>
                  Accetta tutti
                </BrutalButton>
              </div>
            </div>
          ) : (
            <div>
              <h2 className="font-heading text-xl mb-4">
                Gestisci preferenze cookie
              </h2>

              <div className="space-y-4 mb-6">
                <div className="flex items-start justify-between p-3 bg-surface rounded">
                  <div className="flex-1">
                    <h3 className="font-bold mb-1">Cookie necessari</h3>
                    <p className="text-sm text-foreground/70">
                      Essenziali per il funzionamento del sito (autenticazione, sicurezza)
                    </p>
                  </div>
                  <input
                    type="checkbox"
                    checked={preferences.necessary}
                    disabled
                    className="mt-1"
                  />
                </div>

                <div className="flex items-start justify-between p-3 bg-surface rounded">
                  <div className="flex-1">
                    <h3 className="font-bold mb-1">Cookie analitici</h3>
                    <p className="text-sm text-foreground/70">
                      Ci aiutano a capire come gli utenti utilizzano il sito
                    </p>
                  </div>
                  <input
                    type="checkbox"
                    checked={preferences.analytics}
                    onChange={(e) =>
                      setPreferences({ ...preferences, analytics: e.target.checked })
                    }
                    className="mt-1"
                  />
                </div>

                <div className="flex items-start justify-between p-3 bg-surface rounded">
                  <div className="flex-1">
                    <h3 className="font-bold mb-1">Cookie di marketing</h3>
                    <p className="text-sm text-foreground/70">
                      Utilizzati per mostrare contenuti personalizzati
                    </p>
                  </div>
                  <input
                    type="checkbox"
                    checked={preferences.marketing}
                    onChange={(e) =>
                      setPreferences({ ...preferences, marketing: e.target.checked })
                    }
                    className="mt-1"
                  />
                </div>
              </div>

              <div className="flex gap-3">
                <BrutalButton size="sm" onClick={savePreferences}>
                  Salva preferenze
                </BrutalButton>
                <BrutalButton
                  size="sm"
                  variant="outline"
                  onClick={() => setShowPreferences(false)}
                >
                  Indietro
                </BrutalButton>
              </div>
            </div>
          )}
        </BrutalCard>
      </div>
    </div>
  );
};
